/* prettier-ignore */

import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ScrollService } from './scrollContainer.service';
import { DataChatService } from './dataChat.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
// import { IndicatorsService } from 'src/app/pages/custom-route/shared/map/services/indicators.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { IframeComponent } from 'src/app/pages/new-details/components/action-buttons/components/iframe/iframe.component';
import { ModalController } from '@ionic/angular';
import { NewCustomRouteService } from 'src/app/pages/new-custom-route/new-custom-route.service';

@Injectable({ providedIn: 'root' })

export class ChatBotTextAnimationService {
  private customRouteService = inject(NewCustomRouteService);
  private dataChat = inject(DataChatService);
  private scrollService = inject(ScrollService);
  private translateService = inject(TranslateService);
  private zone = inject(NgZone);
  private router = inject(Router);
  // private indicatorsService = inject(IndicatorsService);
  private breakpointsService = inject(BreakpointsService);

  // ---- Estado por completion_id
  private queues = new Map<string, string[]>();     // cola de chars por id
  private rafIds = new Map<string, number>();       // requestAnimationFrame id por id
  private nextAt = new Map<string, number>();       // timestamp para el próximo char por id
  private hiddenCommandsMap = new Map<string, string>(); // Nuevo
  private lastScrollAt = 0;

  // ---- Velocidad ajustable (global)
  private msPerChar = 28;               // Acelerado: de 55ms a 28ms
  private punctuationPauseMs = 110;     // Acelerado: de 220ms a 110ms
  private punctuationRegex = /[.,;:!?…]/;

  private allIdleSubject = new Subject<void>();
  public allIdle$ = this.allIdleSubject.asObservable();
  private idleTimer: any = null;
  private modalController = inject(ModalController);

  /** Cambia la velocidad de tecleo en caliente */
  public setTypingSpeed(onlyText: boolean = false) {
    if (onlyText) {
      this.msPerChar = 15;
      this.punctuationPauseMs = 60;
      return;
    }

    this.msPerChar = 28;
    this.punctuationPauseMs = 110;
  }

  // POI Buffer per ID
  private partialActionBuffers = new Map<string, string>();

  public enqueueText(id: string, text: string, onlyText: boolean = false, hiddenCommand?: string) {
    if (!text) return;

    const safeText = this.processTextForActions(id, text);
    if (!safeText) return; // Todo era acción o quedó en buffer

    if (hiddenCommand) {
      this.hiddenCommandsMap.set(id, hiddenCommand);
    }

    this.setTypingSpeed(onlyText);

    if (!this.queues.has(id))
      this.queues.set(id, []);

    const q = this.queues.get(id)!;

    // FIX CRÍTICO: Fragmentar por palabras en lugar de carácter por carácter
    // Esto mantiene el efecto de escritura gradual sin romper caracteres UTF-16

    // Split por palabras y espacios manteniendo ambos como tokens
    const tokens = safeText.match(/\S+|\s+/g) || [];
    for (const token of tokens) {
      if (token) q.push(token);
    }

    if (!this.rafIds.has(id))
      this.startTypingLoop(id, onlyText);
  }

  private processTextForActions(id: string, text: string): string {
    let buffer = (this.partialActionBuffers.get(id) || "") + text;
    let outputText = "";

    // DEBUG: Log entrada
    if (text.includes('ACTION')) {
      console.log('🔍 [TextAnim] processTextForActions entrada:', { id, textLength: text.length, text: text.substring(0, 200) });
      console.log('🔍 [TextAnim] Buffer completo:', buffer.substring(0, 500));
    }

    // 1. Process Complete Actions - WEATHER PRIMERO (tiene estructura más compleja)
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_WEATHER:', (jsonData) => {
      this.handleWeatherAction(id, jsonData);
    });

    // 2. Luego POI
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_POI:', (jsonData) => {
      console.log('🚀 [TextAnim] POI Action JSON data:', jsonData);
      this.handleNavigateToPoiAction(jsonData);
    });

    // 3. Fallback para WEB (cuando no hay POI oficial)
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_WEB:', (jsonData) => {
      console.log('🚀 [TextAnim] WEB Action JSON data:', jsonData);
      this.handleExternalUrlAction(jsonData);
    });

    // 3. Check for partials at end
    const lastOpen = buffer.lastIndexOf('[');
    if (lastOpen !== -1) {
      const potentialTag = buffer.substring(lastOpen);
      // Si parece que empieza una acción, esperamos al siguiente chunk para cerrarla
      if (potentialTag.startsWith("[ACTION:")) {
        outputText = buffer.substring(0, lastOpen);
        this.partialActionBuffers.set(id, potentialTag);
        // Limpieza final de residuos
        return this.cleanActionResidues(outputText);
      }
    }

    // No hay tag parcial, flush
    this.partialActionBuffers.set(id, "");
    // Limpieza final de residuos
    return this.cleanActionResidues(buffer);
  }

  /** Extrae y maneja un action tag usando balance de llaves para JSON correcto */
  private extractAndHandleActionTag(id: string, buffer: string, prefix: string, handler: (jsonData: any) => void): string {
    let startIdx = buffer.indexOf(prefix);

    while (startIdx !== -1) {
      const jsonStart = startIdx + prefix.length;

      // Encontrar el JSON completo balanceando llaves
      let depth = 0;
      let inString = false;
      let escapeNext = false;
      let jsonEnd = -1;

      for (let i = jsonStart; i < buffer.length; i++) {
        const char = buffer[i];

        if (escapeNext) {
          escapeNext = false;
          continue;
        }

        if (char === '\\' && inString) {
          escapeNext = true;
          continue;
        }

        if (char === '"') {
          inString = !inString;
          continue;
        }

        if (!inString) {
          if (char === '{') depth++;
          if (char === '}') {
            depth--;
            if (depth === 0) {
              jsonEnd = i + 1;
              break;
            }
          }
        }
      }

      // Verificar que el tag está completo (termina con ])
      if (jsonEnd !== -1 && jsonEnd < buffer.length && buffer[jsonEnd] === ']') {
        const jsonString = buffer.substring(jsonStart, jsonEnd);
        console.log(`🎯 [TextAnim] Action Tag extraído (${prefix.substring(8, 20)}): ${jsonString.substring(0, 100)}...`);

        try {
          const jsonData = JSON.parse(jsonString);
          console.log(`✅ [TextAnim] JSON parseado correctamente para ${prefix.substring(8, 20)}`);
          handler(jsonData);
          // Remover el tag del buffer
          buffer = buffer.substring(0, startIdx) + buffer.substring(jsonEnd + 1);
          // Buscar la próxima ocurrencia desde el mismo punto
          startIdx = buffer.indexOf(prefix);
        } catch (e) {
          console.error(`❌ [TextAnim] Error parsing JSON para ${prefix}:`, e);
          // Avanzar para evitar loop infinito
          startIdx = buffer.indexOf(prefix, startIdx + 1);
        }
      } else {
        // El tag no está completo, dejarlo en el buffer para el próximo chunk
        console.log(`⏳ [TextAnim] Tag incompleto ${prefix.substring(8, 20)}, esperando más datos...`);
        break;
      }
    }

    return buffer;
  }

  /** Limpia cualquier residuo de action tags que haya quedado */
  private cleanActionResidues(text: string): string {
    if (!text) return text;

    // Patrones de residuos comunes de action tags mal parseados
    // IMPORTANTE: No eliminar espacios normales, solo caracteres de action tags
    let cleaned = text
      .replace(/^\}?\]?(?=\S)/g, '')         // }] al principio solo si hay texto después
      .replace(/(?<=\S)\}?\]?$/g, '')        // }] al final solo si hay texto antes
      .replace(/\[ACTION:[^\]]*$/g, '')      // Action tags incompletos al final
      .replace(/\}\]\s*\[ACTION:/g, '[ACTION:')  // }] entre action tags

    // Eliminar líneas que solo son basura de action tags
    if (/^[\}\]\[\s]+$/.test(cleaned)) {
      return '';
    }

    return cleaned;
  }

  private handleWeatherAction(id: string, weatherData: any, retryCount: number = 0) {
    console.log('🌤️ [TextAnim] handleWeatherAction llamado con:', { id, weatherData, retryCount });
    console.log('🌤️ [TextAnim] Mensajes actuales:', this.dataChat.messages().map((m: any) => ({ id: m.id, role: m.role, hasWeather: !!m.weatherData })));

    const messages = this.dataChat.messages();
    let index = messages.findIndex((m: any) => m.id === id);

    // Si no encontramos el mensaje por ID, buscar el último mensaje de assistant
    if (index === -1) {
      console.log('🌤️ [TextAnim] No se encontró mensaje con id:', id, '- buscando último assistant...');
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          index = i;
          break;
        }
      }
    }

    if (index !== -1) {
      console.log('🌤️ [TextAnim] ✅ Actualizando mensaje en índice:', index);
      this.dataChat.messages.update((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], weatherData };
        return updated;
      });
    } else if (retryCount < 5) {
      // El mensaje aún no existe, reintentar después de un delay
      console.log(`🌤️ [TextAnim] ⏳ Mensaje no encontrado, reintentando en 300ms (intento ${retryCount + 1}/5)...`);
      setTimeout(() => {
        this.handleWeatherAction(id, weatherData, retryCount + 1);
      }, 300);
    } else {
      console.log('🌤️ [TextAnim] ❌ No se encontró ningún mensaje después de 5 reintentos');
    }
  }

  private async handleNavigateToPoiAction(poiData: any) {
    if (!this.isInPlannerContext()) {
      const poiUrl = poiData.poi_url;
      if (!poiUrl) return;
      
      this.router.navigate([poiUrl]);
    } else {
      const poiId = Number(poiData.poi_id);
      const poiType = poiData.poi_type === 'event' ? 'event' : 'point';

      if (!poiId) {
        console.warn('⚠️ [TextAnim] POI sin ID válido para planner, skipping');
        return;
      }

      const item = {
        id: poiId,
        type: poiType
      };

      console.log(`📍 [TextAnim] Planner context → Opening NewDetailsPage for: ${poiData.poi_name} (ID: ${poiId}, type: ${poiType})`);
      this.customRouteService.modalItemInfo(item);
      //TODO: Show details info


      // if (this.breakpointsService.isMobile()) {
      //   this.indicatorsService.openBottomSheetDetailsMobile(poiId, poiType);
      // } else {
      //   this.indicatorsService.openBottomSheetDetailsDesktop(poiId, poiType);
      // }
    }

    //// --- Contexto general: abrir poiSidebar (iframe) ---
    //// Validate URL before adding to list
    //const poiUrl = poiData.poi_url || poiData.url;

    //// Skip if no URL or if URL is not absolute (must start with http/https)
    //if (!poiUrl) {
    //  console.warn('⚠️ [TextAnim] POI has no URL, skipping');
    //  return;
    //}

    //if (!poiUrl.startsWith('http://') && !poiUrl.startsWith('https://') && !poiUrl.startsWith('/')) {
    //  console.warn('⚠️ [TextAnim] POI URL is not valid (must be absolute or start with /), skipping:', poiUrl);
    //  return;
    //}

    //// Validate URL is reachable (with timeout) - skip CORS failures silently
    //try {
    //  const controller = new AbortController();
    //  const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

    //  const response = await fetch(poiUrl, {
    //    method: 'HEAD', // Use HEAD to avoid downloading the page
    //    signal: controller.signal,
    //    mode: 'no-cors' // Allow cross-origin but we can't check status
    //  });

    //  clearTimeout(timeoutId);
    //} catch (e: any) {
    //  if (e.name === 'AbortError') {
    //    console.warn('⚠️ [TextAnim] POI URL timeout, skipping:', poiUrl);
    //    return;
    //  }
    //  console.log('ℹ️ [TextAnim] POI URL prefetch failed (might be CORS), proceeding anyway:', poiUrl);
    //}

    //this.dataChat.poiList.update(list => {
    //  const existsIndex = list.findIndex(p => p.poi_id === poiData.poi_id);
    //  if (existsIndex === -1) {
    //    const newList = [...list, poiData];
    //    this.dataChat.activePoiIndex.set(newList.length - 1);
    //    this.dataChat.isPoiSidebarOpen.set(true);
    //    console.log('✨ [TextAnim] New POI added. Sidebar OPENED.', newList);
    //    return newList;
    //  } else {
    //    this.dataChat.activePoiIndex.set(existsIndex);
    //    this.dataChat.isPoiSidebarOpen.set(true);
    //    console.log('🔄 [TextAnim] Existing POI activated. Sidebar OPENED.');
    //    return list;
    //  }
    //});
  }

  /** Detecta si estamos en la vista del planner/custom-route */
  private isInPlannerContext(): boolean {
    return this.router.url.includes('custom-route');
  }

  /** Bucle de tecleo letra a letra con rAF y control temporal */
  private startTypingLoop(id: string, onlyText: boolean) {
    this.nextAt.delete(id); // para arrancar “ya” en el primer frame

    const tick = (now: number) => {
      const q = this.queues.get(id);

      if (!q || q.length === 0) {
        const rafId = this.rafIds.get(id);
        if (rafId != null) cancelAnimationFrame(rafId);
        this.rafIds.delete(id);
        this.nextAt.delete(id);
        this.zone.run(() => {
          this.throttledScroll();
          this.maybeEmitAllIdle();
        });
        return;
      }

      // Inicializa el "cuando" para el primer char
      if (!this.nextAt.has(id))
        this.nextAt.set(id, now);

      let when = this.nextAt.get(id)!;
      let chunk = '';
      let emitted = 0;

      // FIX: Quitamos el 'while' agresivo para que no se coma todos los tokens en un frame
      // Procesamos máximo 1-2 tokens por frame si ha pasado el tiempo necesario
      if (q.length > 0 && now >= when) {
        const token = q.shift()!;
        chunk += token;
        emitted++;

        // Calcular delay: proporcional a la longitud del token para que parezca natural
        // msPerChar es ~28ms. Si la palabra tiene 5 letras, esperamos ~140ms
        let delay = token.length * this.msPerChar;

        // Pausa extra si el token termina en puntuación
        const lastChar = token.trim().slice(-1);
        if (this.punctuationRegex.test(lastChar)) {
          delay += this.punctuationPauseMs;
        }

        when += delay;
      }

      // Si hay algo que añadir, haz un único update
      if (emitted > 0) {
        const chunkToAppend = chunk; // cierra sobre el valor
        this.zone.run(() => {
          this.handleAssistenQueueMessage(id, chunkToAppend);
          this.throttledScroll();
        });
        this.nextAt.set(id, when);
      }

      // reprogramar
      const rafId = requestAnimationFrame(tick);
      this.rafIds.set(id, rafId);
    };

    this.zone.runOutsideAngular(() => {
      const rafId = requestAnimationFrame(tick);
      this.rafIds.set(id, rafId);
    });
  }

  private maybeEmitAllIdle() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    const scheduleCheck = () => {
      const anyQueueHasItems = [...this.queues.values()].some(q => q.length > 0);
      const anyRAF = this.rafIds.size > 0;
      if (!anyQueueHasItems && !anyRAF)
        this.allIdleSubject.next();
    };

    requestAnimationFrame(() => {
      this.idleTimer = setTimeout(scheduleCheck, 40);
    });
  }

  /** Scroll con throttle para no forzar layout por letra */
  private throttledScroll() {
    const now = performance.now();
    if (now - this.lastScrollAt > 48) { // ~20fps
      this.scrollToBottom();
      this.lastScrollAt = now;
    }
  }

  createUserMessage(message: string) {
    if (!message || !message.trim()) return;

    this.dataChat.messages.update((prev) => {
      // Evitar duplicados exactos si se llama dos veces seguidas (ej: voz + websocket)
      const lastMsg = prev.length > 0 ? prev[prev.length - 1] : null;
      if (lastMsg && lastMsg.role === 'user' && lastMsg.content === message) {
        return prev;
      }
      return [...prev, { role: 'user', content: message, show: true }];
    });

    this.scrollToBottom();
  }

  handleAssistenQueueMessage(id: string, text: string) {
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      const isProactive = id.includes('_tip');
      const hiddenCommand = this.hiddenCommandsMap.get(id);
      if (hiddenCommand) this.hiddenCommandsMap.delete(id);

      if (index !== -1) {
        // Mensaje ya existe → actualizar contenido (chunk append)
        const updated = [...prev];
        const target = updated[index];

        if (isProactive) {
          // Actualizar el proactiveChildren del padre
          const parentIdx = this._findParentBotIndex(updated, index);
          if (parentIdx !== -1) {
            const parent = updated[parentIdx];
            const children = [...(parent.proactiveChildren || [])];
            const childIdx = children.findIndex(c => c.id === id);
            if (childIdx !== -1) {
              children[childIdx] = {
                ...children[childIdx],
                content: (children[childIdx].content ?? '') + text,
                hiddenCommand: hiddenCommand || children[childIdx].hiddenCommand
              };
            }
            updated[parentIdx] = { ...parent, proactiveChildren: children };
          }
        }

        updated[index] = {
          ...target,
          content: (target.content ?? '') + text,
          isProactive,
          hiddenCommand: hiddenCommand || target.hiddenCommand
        };
        return updated;
      }

      // Mensaje nuevo
      const newMsg: any = { role: 'assistant', content: text, show: true, id, isProactive, hiddenCommand };

      if (isProactive) {
        // Adjuntar al último mensaje bot como proactiveChildren[] y ocultar el standalone
        const updated = [...prev];
        const parentIdx = this._findLastBotIndex(updated);
        if (parentIdx !== -1) {
          const parent = updated[parentIdx];
          const children = [...(parent.proactiveChildren || [])];
          children.push({ ...newMsg });
          updated[parentIdx] = { ...parent, proactiveChildren: children };
          // Añadir al array con show:false (oculto, solo para tracking de chunks)
          return [...updated, { ...newMsg, show: false }];
        }
      }

      return [...prev, newMsg];
    });
  }

  /** Encuentra el índice del mensaje bot padre de un proactivo (el bot msg justo antes) */
  private _findParentBotIndex(messages: any[], proactiveIdx: number): number {
    for (let i = proactiveIdx - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].isProactive) return i;
    }
    return -1;
  }

  /** Encuentra el último mensaje bot no-proactivo */
  private _findLastBotIndex(messages: any[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].isProactive) return i;
    }
    return -1;
  }

  handleAssistenAllMessage(id: string, text: string) {
    if (!text || text === '[DONE]') return;
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      if (index !== -1) {
        const updated = [...prev];
        const targetMessage = updated[index];
        updated[index] = { ...targetMessage, content: text };
        return updated;
      }
      return prev;
    });
  }

  handleAllTextOnStop(text: string) {
    if (!text || text === '[DONE]') return;
    this.dataChat.messages.update((prev) => {
      const lastAssistantIndex = [...prev].reverse().findIndex((m) => m.role === 'assistant');
      if (lastAssistantIndex !== -1) {
        const actualIndex = prev.length - 1 - lastAssistantIndex;
        const updated = [...prev];
        updated[actualIndex] = { ...updated[actualIndex], content: text, show: true };
        return updated;
      }
      return prev;
    });
    this.scrollToBottom();
  }

  showConnectionErrorMessage() {
    if (this.dataChat?.messages()?.[this.dataChat?.messages()?.length - 1].danger)
      return;

    this.dataChat.messages.update((prev) => [
      ...prev,
      {
        content: this.translateService.instant('Fallo en la comunicación, vuelve a intentarlo'),
        role: 'assistant',
        show: true,
        danger: true,
      },
    ]);
  }

  public cancel(id: string, addEllipsis = false) {
    const rafId = this.rafIds?.get(id);
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      this.rafIds.delete(id);
    }

    this.queues?.delete(id);
    this.nextAt?.delete(id);

    if (addEllipsis) {
      this.dataChat.messages.update((prev) => {
        const index = prev.findIndex((m: any) => m.id === id);
        if (index !== -1) {
          const updated = [...prev];
          const target = updated[index];
          if (target.content && !target.content.endsWith('....')) {
            updated[index] = { ...target, content: target.content.trim() + '....' };
          }
          return updated;
        }
        return prev;
      });
    }

    this.zone.run(() => this.maybeEmitAllIdle());
  }

  public scrollToBottom() {
    setTimeout(() => {
      this.scrollService.scrollToBottom();
    }, 0);
  }

  private async handleExternalUrlAction(webData: any) {
    const url = webData.url;
    if (!url) return;

    // Transformar a formato compatible con poiList
    const virtualPoi = {
      poi_id: `web_${btoa(url).substring(0, 16)}`, // ID estable para la URL
      poi_name: webData.title || 'Sitio Web',
      poi_url: url,
      image: null,
      is_fallback_web: true
    };

    const iframeData = {
      title: webData.title || 'Sitio Web',
      url,
    };
    this.openIframeFull(iframeData);
    return;

    this.dataChat.poiList.update(list => {
      const existsIndex = list.findIndex((p: any) => p.poi_url === url);
      if (existsIndex === -1) {
        const newList = [...list, virtualPoi];
        this.dataChat.activePoiIndex.set(newList.length - 1);
        this.dataChat.isPoiSidebarOpen.set(true);
        return newList;
      } else {
        this.dataChat.activePoiIndex.set(existsIndex);
        this.dataChat.isPoiSidebarOpen.set(true);
        return list;
      }
    });
  }

  async openIframeFull(item){
    const iframeModal = await this.modalController.create({
      component: IframeComponent,
      cssClass: "iframe_fullscreen",
      componentProps: {
        url: item.url,
        title: item.title,
      },
    });
    await iframeModal.present();
  }

}